import { useState, useEffect } from 'react';
import AdminHeader from '../../components/adminHeader';
import AdminFooter from '../../components/adminFooter';
import EditMailTemplateModal from '../../components/modal/EditMailTemplateModal';
import CreateMailTemplateModal from '../../components/modal/CreateMailTemplateModal';
import ViewMailTemplateModal from '../../components/modal/ViewMailTemplateModal';

interface MailTemplate {
  id: number;
  name: string;
  subject: string;
  body: string; // HTML content of the template
  created_at: string;
}

const MailSMTP = () => {
  const [mailTemplates, setMailTemplates] = useState<MailTemplate[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTemplateBody, setSelectedTemplateBody] = useState('');
  const [isTestFormVisible, setIsTestFormVisible] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testSubject, setTestSubject] = useState('');
  const [testBody, setTestBody] = useState('');
  const [previewMode, setPreviewMode] = useState('desktop'); // 'desktop' or 'mobile'
  const [selectedFiles, setSelectedFiles] = useState<FileList | null>(null);

  // Mock data for mail templates
  const mockMailTemplates: MailTemplate[] = [
    { id: 1, name: 'OTP', subject: 'Your One-Time Password', body: '<p>Your OTP is: <strong>123456</strong></p>', created_at: new Date().toISOString() },
    { id: 2, name: 'Winners', subject: "Congratulations! You've Won!", body: "<h1>You are a winner!</h1><p>Claim your prize now.</p>", created_at: new Date().toISOString() },
    { id: 3, name: 'Thanks', subject: 'Thank You for Your Purchase', body: '<p>We appreciate your business.</p>', created_at: new Date().toISOString() },
    { id: 4, name: 'Engagement', subject: 'We Miss You!', body: '<p>Come back and check out our new features.</p>', created_at: new Date().toISOString() },
    { id: 5, name: 'Giveaway', subject: 'New Giveaway Alert!', body: '<p>Participate in our latest giveaway.</p>', created_at: new Date().toISOString() },
    { id: 6, name: 'Twitch Marketors Welcome', subject: 'Welcome to the Team!', body: '<p>Glad to have you on board, Twitch Marketor!</p>', created_at: new Date().toISOString() },
    { id: 7, name: 'Sorry', subject: 'Apology from Our Team', body: '<p>We apologize for the inconvenience.</p>', created_at: new Date().toISOString() },
    { id: 8, name: 'Announcement', subject: 'Important Announcement', body: '<p>Read the latest news from us.</p>', created_at: new Date().toISOString() },
  ];

  const filteredMailTemplates = mailTemplates.filter(template =>
    template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    template.subject.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [currentMailTemplate, setCurrentMailTemplate] = useState<MailTemplate | null>(null);

  const fetchMailTemplates = () => {
    // Since no backend, use mock data
    setMailTemplates(mockMailTemplates);
  };

  const handleCreateMailTemplate = (newTemplate: Omit<MailTemplate, 'id' | 'created_at'>) => {
    // For now, just add to mock data
    const newId = Math.max(...mailTemplates.map(t => t.id)) + 1;
    const templateWithId = { ...newTemplate, id: newId, created_at: new Date().toISOString() };
    setMailTemplates([...mailTemplates, templateWithId]);
    setIsCreateModalOpen(false);
  };

  const handleUpdateMailTemplate = (id: number, updatedTemplate: Partial<MailTemplate>) => {
    setMailTemplates(mailTemplates.map(template =>
      template.id === id ? { ...template, ...updatedTemplate } : template
    ));
    setIsEditModalOpen(false);
  };

  const handleDeleteMailTemplate = (id: number) => {
    if (window.confirm('Are you sure you want to delete this mail template?')) {
      setMailTemplates(mailTemplates.filter(template => template.id !== id));
    }
  };

  const openEditModal = (template: MailTemplate) => {
    setCurrentMailTemplate(template);
    setIsEditModalOpen(true);
  };

  const openViewModal = (template: MailTemplate) => {
    setCurrentMailTemplate(template);
    setIsViewModalOpen(true);
  };

  const handleTemplateSelect = (template: MailTemplate) => {
    setSelectedTemplateBody(template.body);
    setTestSubject(template.subject);
    setTestBody(template.body);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedFiles(e.target.files);
  };

  const handleSendTestEmail = () => {
    let attachmentNames = 'No attachments';
    if (selectedFiles && selectedFiles.length > 0) {
      attachmentNames = Array.from(selectedFiles).map(file => file.name).join(', ');
    }
    alert(`Sending test email to: ${testEmail}
Subject: ${testSubject}
Body: ${testBody}
Attachments: ${attachmentNames}`);
    // In a real application, you would integrate with an email sending service here.
  };

  useEffect(() => {
    fetchMailTemplates();
  }, []);

  return (
    <div className="w-full h-full flex flex-col bg-[#00000071]">
      <AdminHeader />
      <div
        style={{
          backgroundImage: "url(/assets/img/landing/landing-bg.png)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
        className="flex-grow flex flex-col items-center justify-center w-full relative pt-6 px-4"
      >
        <div className="w-full max-w-6xl gothic-panel">
          <h1 className="gothic-heading">Manage Mail Templates</h1>
          <div className="flex justify-between mb-4">
            <button onClick={() => setIsCreateModalOpen(true)} className="gothic-button gothic-button-primary">
              Add New Template
            </button>
            <input
              type="text"
              placeholder="Search templates..."
              className="gothic-input w-1/3"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="overflow-x-auto mt-8">
            <table className="min-w-full gothic-panel border-collapse">
              <thead>
                <tr className="bg-gray-800">
                  <th className="py-3 px-4 text-left gothic-text-silver">ID</th>
                  <th className="py-3 px-4 text-left gothic-text-silver">Name</th>
                  <th className="py-3 px-4 text-left gothic-text-silver">Subject</th>
                  <th className="py-3 px-4 text-left gothic-text-silver">Created At</th>
                  <th className="py-3 px-4 text-left gothic-text-silver">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredMailTemplates.map((template) => (
                  <tr key={template.id} className="border-t border-gray-700 hover:bg-gray-800 transition duration-200">
                    <td className="py-3 px-4 gothic-text-gold">{template.id}</td>
                    <td className="py-3 px-4 gothic-text-silver">{template.name}</td>
                    <td className="py-3 px-4 gothic-text-silver">{template.subject}</td>
                    <td className="py-3 px-4 gothic-text-silver">{new Date(template.created_at).toLocaleString()}</td>
                    <td className="py-3 px-4 flex flex-wrap gap-2">
                      <button onClick={() => handleTemplateSelect(template)} className="gothic-button bg-gray-700 border-gray-600">
                        Select
                      </button>
                      <button onClick={() => openViewModal(template)} className="gothic-button bg-gray-700 border-gray-600">
                        View
                      </button>
                      <button onClick={() => openEditModal(template)} className="gothic-button bg-gray-700 border-gray-600">
                        Edit
                      </button>
                      <button onClick={() => handleDeleteMailTemplate(template.id)} className="gothic-button bg-gray-700 border-gray-600">
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-8">
            <h2 className="gothic-heading">HTML Template Editor & Preview</h2>
            <textarea
              className="gothic-input w-full h-64 mb-4"
              value={selectedTemplateBody}
              onChange={(e) => setSelectedTemplateBody(e.target.value)}
              placeholder="Edit HTML template here..."
            ></textarea>
            <h3 className="gothic-heading">Preview:</h3>
            <div className="flex justify-center mb-4">
              <button
                onClick={() => setPreviewMode('desktop')}
                className={`gothic-button ${previewMode === 'desktop' ? 'gothic-button-primary' : 'bg-gray-700 border-gray-600'} mr-2`}
              >
                Desktop View
              </button>
              <button
                onClick={() => setPreviewMode('mobile')}
                className={`gothic-button ${previewMode === 'mobile' ? 'gothic-button-primary' : 'bg-gray-700 border-gray-600'}`}
              >
                Mobile View
              </button>
            </div>
            <div
              className={`gothic-panel p-4 border border-gray-700 bg-gray-900 overflow-auto ${previewMode === 'mobile' ? 'w-80 mx-auto' : 'w-full'}`}
              style={{ maxWidth: previewMode === 'mobile' ? '320px' : '100%' }}
              dangerouslySetInnerHTML={{ __html: selectedTemplateBody }}
            ></div>
          </div>

          <div className="mt-8">
            <button
              onClick={() => setIsTestFormVisible(!isTestFormVisible)}
              className="gothic-button gothic-button-primary mb-4"
            >
              {isTestFormVisible ? 'Hide Test Sending Form' : 'Show Test Sending Form'}
            </button>

            {isTestFormVisible && (
              <div className="gothic-panel p-4">
                <h2 className="gothic-heading">Test Sending Form</h2>
                <div className="mb-4">
                  <label htmlFor="testEmail" className="block gothic-text-silver mb-2">Recipient Email:</label>
                  <input
                    type="email"
                    id="testEmail"
                    className="gothic-input w-full"
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                    placeholder="test@example.com"
                  />
                </div>
                <div className="mb-4">
                  <label htmlFor="testSubject" className="block gothic-text-silver mb-2">Subject:</label>
                  <input
                    type="text"
                    id="testSubject"
                    className="gothic-input w-full"
                    value={testSubject}
                    onChange={(e) => setTestSubject(e.target.value)}
                    placeholder="Test Subject"
                  />
                </div>
                <div className="mb-4">
                  <label htmlFor="testBody" className="block gothic-text-silver mb-2">Body (HTML):</label>
                  <textarea
                    id="testBody"
                    className="gothic-input w-full h-32"
                    value={testBody}
                    onChange={(e) => setTestBody(e.target.value)}
                    placeholder="Test Email Body (HTML)"
                  ></textarea>
                </div>
                <div className="mb-4">
                  <label htmlFor="attachments" className="block gothic-text-silver mb-2">Attachments:</label>
                  <input
                    type="file"
                    id="attachments"
                    multiple
                    onChange={handleFileChange}
                    className="gothic-input w-full"
                  />
                </div>
                <button onClick={handleSendTestEmail} className="gothic-button gothic-button-primary">
                  Send Test Email
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      {isCreateModalOpen && (
        <CreateMailTemplateModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onCreate={handleCreateMailTemplate}
        />
      )}
      {isEditModalOpen && currentMailTemplate && (
        <EditMailTemplateModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          mailTemplate={currentMailTemplate}
          onSave={handleUpdateMailTemplate}
        />
      )}
      {isViewModalOpen && currentMailTemplate && (
        <ViewMailTemplateModal
          isOpen={isViewModalOpen}
          onClose={() => setIsViewModalOpen(false)}
          mailTemplate={currentMailTemplate}
        />
      )}
      <AdminFooter />
    </div>
  );
};

export default MailSMTP;
